import {inject, Injectable} from '@angular/core';
import {HistoryStoreItem} from '../../models/history/history-store-item.model';
import {AuthQuery} from '../../queries/auth.query';
import {HistoryItem} from '../../models/history/history-item.model';
import {IconService} from '../icon/icon.service';
import {AppQuery} from '../../queries/app.query';
import {TranslateService} from '@ngx-translate/core';
import {AppService} from '../app/app.service';

@Injectable({
    providedIn: 'root'
})
export class HistoryService {
    private readonly appQuery = inject(AppQuery);
    private readonly appService = inject(AppService);
    private readonly authQuery = inject(AuthQuery);
    private readonly translateService = inject(TranslateService);
    private readonly maxHistoryLength = 100;

    public addNavigationHistory(historyItem: HistoryItem, originalPath: Array<string> = []): void {
        let oldHistory = this.appQuery.getNavigationHistory();
        const path = [...originalPath];
        if (path[0] === 's') {
            path.shift();
        }
        if (oldHistory.length === 0 && path.join('/') !== [
            'me',
            'vaults'
        ].join('/')) {
            this.addDefaultEntryToHistory();
            oldHistory = this.appQuery.getNavigationHistory();
        }

        const latestHistoryItem = oldHistory.at(0);
        if (latestHistoryItem?.path?.join('/') === path.join('/')) {
            if (latestHistoryItem.title === historyItem.title && latestHistoryItem.subTitle === historyItem.subTitle) {
                // prevent adding same item twice
                return;
            }

            if (this.shouldOverwriteLatestHistoryItem(latestHistoryItem, historyItem)) {
                // overwrite old history item if path is equal but title/subTitle differs
                oldHistory[0] = {
                    id: oldHistory[0].id,
                    path, ...historyItem
                };
                this.appService.setNavigationHistory([...oldHistory]);
                this.appService.hideHistoryOverlay();
                return;
            }
        }

        // remove last item when max length is reached
        if (oldHistory.length > this.maxHistoryLength) {
            oldHistory.pop();
        }
        const nextId = oldHistory.length > 0 ? oldHistory[0].id + 1 : 1;
        const navigationItem: HistoryStoreItem = {
            id: nextId,
            path,
            ...historyItem
        };
        this.appService.setNavigationHistory([
            navigationItem,
            ...oldHistory
        ]);
        this.appService.hideHistoryOverlay();
    }

    public removeHistoryItemsWithPathContaining(value: string): void {
        const oldHistory = structuredClone(this.appQuery.getNavigationHistory());
        const newHistory = oldHistory.filter((item: HistoryStoreItem) => !item.path.includes(value));
        this.appService.setNavigationHistory(newHistory);

        const indexIds = [];
        for (const [index, item] of newHistory.entries()) {
            if (newHistory[index + 1] && JSON.stringify(item.path) === JSON.stringify(newHistory[index + 1].path)) {
                indexIds.push(newHistory[index + 1].id);
            }
        }
        for (const duplicatePathId of indexIds) {
            this.removeHistoryItemById(duplicatePathId);
        }
    }

    public removeLatestHistoryItem(): void {
        const oldHistory = structuredClone(this.appQuery.getNavigationHistory());
        oldHistory.shift();
        this.appService.setNavigationHistory([...oldHistory]);
    }

    public clearHistory(): void {
        this.appService.setNavigationHistory([]);
    }

    private removeHistoryItemById(id: number): void {
        const oldHistory = this.appQuery.getNavigationHistory();
        const updatedHistory = oldHistory.filter((item: HistoryStoreItem) => item.id !== id);
        this.appService.setNavigationHistory(updatedHistory);
    }

    private shouldOverwriteLatestHistoryItem(latestHistoryItem: HistoryStoreItem, historyItem: HistoryItem): boolean {
        const inaccessibleDocumentTitle = this.translateService.instant('HISTORY.DOCUMENTS_INACCESSIBLE_HISTORY_ITEM_NAME');
        if (latestHistoryItem.title === inaccessibleDocumentTitle || historyItem.title === inaccessibleDocumentTitle) {
            return true;
        }

        if (!latestHistoryItem.queryParams && !historyItem.queryParams) {
            return true;
        }

        if (latestHistoryItem.queryParams && historyItem.queryParams) {
            const oldKeys = Object.keys(latestHistoryItem.queryParams);
            const newKeys = Object.keys(historyItem.queryParams);
            if (oldKeys.length === newKeys.length && oldKeys.every(key => latestHistoryItem.queryParams![key] === historyItem.queryParams![key])) {
                return true;
            }
        }

        return false;
    }

    private addDefaultEntryToHistory(): void {
        const defaultEntry = {
            id: 0,
            title: 'FAST_ACCESS.ME',
            subTitle: 'VAULTS',
            icon: IconService.getUserIconUrl(this.authQuery.getUserId()),
            path: [
                'me',
                'vaults'
            ]
        };
        this.appService.setNavigationHistory([defaultEntry]);
    }
}
